import React, { useEffect, useState, useRef } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, SaveOutlined, SyncOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Tabs, Form, Button, message, Checkbox, Space, Divider, Card, Spin, Row, Col, FloatButton, Alert, Switch } from 'antd';
import { useLocation } from 'react-router-dom';
import { syncOdoo } from '../../../../apis/OdooSyncApi';
import { getConfig, saveConfig } from '../../../../apis/ConfiguracionAplicacionApi';
import './styles.scss';

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Configuración de Aplicación</span>
      </>
    ),
  },
];

const ODOO_MODELS = [
  { key: 'res.company', label: 'Empresas (res.company)' },
  { key: 'res.partner', label: 'Contactos (res.partner)' },
  { key: 'hr.employee', label: 'Empleados (hr.employee)' },
  { key: 'hr.leave', label: 'Ausencias (hr.leave)' },
  { key: 'hr.leave.type', label: 'Tipos de ausencia (hr.leave.type)' },
  { key: 'account.account', label: 'Cuentas Contables (account.account)' },
  { key: 'account.analytic.account', label: 'Cuentas Analíticas (account.analytic.account)' },
  { key: 'hr.department', label: 'Departamentos (hr.department)' },
  { key: 'hr.job', label: 'Puestos (hr.job)' },
  { key: 'resource.calendar', label: 'Horarios (resource.calendar)' },
];

const ConfiguracionAplicacion = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [syncAll, setSyncAll] = useState(true);
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const [odooActive, setOdooActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const effectRan = useRef(false);

  useEffect(() => {
    if (!effectRan.current) {
      loadConfig();
    }
    return () => (effectRan.current = true);
  }, []);

  useEffect(() => {
    if (!location?.search) {
      return;
    }

    const query = new URLSearchParams(location.search);
    const syncAllParam = query.get('syncAll');
    const modelsParam = query.get('models');

    if (syncAllParam == null && !modelsParam) {
      return;
    }

    let nextSyncAll = true;
    if (syncAllParam != null) {
      const normalized = String(syncAllParam).trim().toLowerCase();
      nextSyncAll = !['false', '0', 'no'].includes(normalized);
    }

    const nextSelectedModels = (modelsParam || '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0 && ODOO_MODELS.some((model) => model.key === item));

    setSyncAll(nextSyncAll);
    setSelectedModels(nextSyncAll ? [] : nextSelectedModels);
  }, [location.search]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await getConfig();
      if (response.ok && response.payload) {
        const integraciones = response.payload.integraciones || {};
        setOdooActive(integraciones.odoo?.active || false);
      } else {
        setOdooActive(false);
      }
    } catch (error) {
      console.error('Error loading config:', error);
      setOdooActive(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIntegraciones = async () => {
    try {
      setSaving(true);
      const payload = {
        integraciones: {
          odoo: { active: odooActive },
        },
      };
      const response = await saveConfig(payload);
      if (response.ok) {
        message.success('Configuración guardada correctamente');
      } else {
        message.error('Error al guardar la configuración');
      }
    } catch (error) {
      message.error(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAllChange = (e) => {
    setSyncAll(e.target.checked);
    if (e.target.checked) {
      setSelectedModels([]);
    }
  };

  const handleModelChange = (key) => {
    setSelectedModels((prev) => {
      if (prev.includes(key)) {
        return prev.filter((m) => m !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const payload = {
        models: syncAll ? null : selectedModels,
        batch_size: 500,
        deactivate_missing_accounts: true,
      };

      const response = await syncOdoo(payload);
      if (response.ok) {
        message.success('Sincronización iniciada correctamente');
        setLastSyncInfo({
          timestamp: new Date().toLocaleString(),
          type: syncAll ? 'Todos los modelos' : `Modelos seleccionados: ${selectedModels.length}`,
        });
      } else {
        message.error('Error al iniciar la sincronización');
      }
    } catch (error) {
      message.error(`Error: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const integracionesContent = (
    <div style={{ padding: '24px' }}>
      <Card title="Configuración de Integraciones" style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={12}>
            <Card type="inner" title="Odoo">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Integración con Odoo activa</span>
                <Switch
                  checked={odooActive}
                  onChange={(checked) => setOdooActive(checked)}
                  checkedChildren="Activa"
                  unCheckedChildren="Inactiva"
                />
              </div>
              <Alert
                type={odooActive ? 'success' : 'warning'}
                message={odooActive ? 'Los procesos enviarán datos a Odoo' : 'Los procesos NO enviarán datos a Odoo'}
                style={{ marginTop: 16 }}
                showIcon
              />
            </Card>
          </Col>
        </Row>
        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveIntegraciones}
            loading={saving}
          >
            Guardar Configuración
          </Button>
        </Form.Item>
      </Card>

      {odooActive && (
        <Card title="Sincronización Odoo" style={{ marginBottom: 24 }}>
          <Alert
            type="warning"
            message="Sincroniza datos desde Odoo hacia el sistema de nómina"
            description="Al sincronizar, se actualizarán los datos de Odoo en el sistema de nómina. Los cambios no son reversibles por lo que recomendamos asegurarte de que los datos en Odoo estén correctos antes de sincronizar."
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider />

          <Form layout="vertical">
            <Form.Item>
              <Checkbox
                checked={syncAll}
                onChange={handleSyncAllChange}
                style={{ marginBottom: 16 }}
              >
                <strong>Sincronizar todos los modelos</strong>
              </Checkbox>
            </Form.Item>

            {!syncAll && (
              <Form.Item label="Seleccionar modelos específicos" style={{ marginBottom: 16 }}>
                <div style={{ 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px', 
                  padding: '12px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {ODOO_MODELS.map((model) => (
                    <div key={model.key} style={{ marginBottom: 8 }}>
                      <Checkbox
                        checked={selectedModels.includes(model.key)}
                        onChange={() => handleModelChange(model.key)}
                      >
                        {model.label}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                {selectedModels.length === 0 && !syncAll && (
                  <p style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 8 }}>
                    Selecciona al menos un modelo
                  </p>
                )}
              </Form.Item>
            )}

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<SyncOutlined spin={syncing} />}
                  onClick={handleSync}
                  loading={syncing}
                  disabled={!syncAll && selectedModels.length === 0}
                  size="large"
                >
                  {syncing ? 'Sincronizando...' : 'Iniciar Sincronización'}
                </Button>
              </Space>
            </Form.Item>

            {lastSyncInfo && (
              <>
                <Divider />
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f6ffed', 
                  border: '1px solid #b7eb8f',
                  borderRadius: '4px'
                }}>
                  <p style={{ margin: '0 0 8px 0', color: '#52c41a' }}>
                    <CheckCircleOutlined /> Última sincronización iniciada:
                  </p>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
                    <strong>Hora:</strong> {lastSyncInfo.timestamp}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px' }}>
                    <strong>Tipo:</strong> {lastSyncInfo.type}
                  </p>
                </div>
              </>
            )}
          </Form>
        </Card>
      )}
    </div>
  );

  return (
    <PageApp routes={routes} title="Configuración de Aplicación">
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="integraciones"
          items={[
            {
              key: 'integraciones',
              label: 'Integraciones',
              children: integracionesContent,
            },
          ]}
        />
      </Spin>
    </PageApp>
  );
};

export default ConfiguracionAplicacion;
